<template>
	<div class="cs">
		<!-- Page header -->
		<div class="cs-head">
			<div>
				<h1 class="cs-title">Accounts</h1>
				<p class="cs-sub">Every account across Town Web, HeyGov and ClerkMinutes, backed by per-brand subscriptions.</p>
			</div>
			<div class="cs-actions">
				<button class="pill" @click="saveView"><i class="fa-regular fa-bookmark"></i> Save view</button>
				<button class="pill" @click="exportCsv"><i class="fa-regular fa-arrow-down-to-line"></i> Export</button>
			</div>
		</div>

		<!-- Brand tabs (All first) -->
		<div class="tabs">
			<a v-for="t in brandTabs" :key="t.key" class="tab" :class="{ active: isTabActive(t.key) }" href="#" @click.prevent="selectBrand(t.key)">
				{{ t.name }}
				<span class="cpill" :title="t.key === 'all' ? 'All accounts (any brand, any status)' : ('Accounts with a ' + t.name + ' subscription, any status')">{{ tabCount(t.key) }}</span>
			</a>
		</div>

		<!-- Main card -->
		<div class="card">
			<div class="card-head">
				<div>
					<div class="card-title">{{ scopeName }}</div>
					<div class="card-sub">
						<template v-if="!loading">{{ (pagination && pagination.total) || 0 }} account<template v-if="((pagination && pagination.total) || 0) !== 1">s</template><template v-if="activeChips.length"> match these filters</template></template>
						<template v-else>Loading…</template>
					</div>
				</div>
			</div>

			<!-- Toolbar: filters (left, primary) + search + status quick filters -->
			<div class="toolbar">
				<button class="pill primary-ghost" :class="{ on: modal }" @click="modal = true">
					<i class="fa-regular fa-sliders"></i> Filters
					<span v-if="activeChips.length" class="badge">{{ activeChips.length }}</span>
				</button>
				<button v-if="activeChips.length" class="pill clear-btn" @click="clearAll"><i class="fa-solid fa-xmark"></i> Clear all</button>
				<div class="colwrap">
					<button class="pill" :class="{ on: colMenu }" @click="colMenu = !colMenu"><i class="fa-regular fa-table-columns"></i> Columns</button>
					<div v-if="colMenu" class="colmenu-back" @click="colMenu = false"></div>
					<div v-if="colMenu" class="colmenu">
						<div class="colmenu-title">Show columns</div>
						<label v-for="col in columnDefs" :key="col.key" class="colrow">
							<input type="checkbox" v-model="cols[col.key]" :disabled="col.always" @change="saveCols" />
							<span>{{ col.label }}</span>
						</label>
					</div>
				</div>
				<div class="search">
					<i class="fa-regular fa-magnifying-glass"></i>
					<input type="text" placeholder="Search name, domain or county" v-model="search" @input="debouncedReload" @keyup.enter="reload()" />
				</div>
				<div class="status-quick">
					<span v-for="s in visibleStatuses" :key="s.value" class="spill" :class="['s-' + s.value, { on: statuses.includes(s.value) }]" @click="toggleStatus(s.value)">
						<span class="dot"></span>{{ s.label }}
						<span class="n">{{ facets.statuses[s.value] || 0 }}</span>
					</span>
				</div>
			</div>

			<!-- Active filter chips -->
			<div v-if="activeChips.length" class="chips">
				<span v-for="chip in activeChips" :key="chip.key" class="chip">
					{{ chip.label }}<i class="fa-solid fa-xmark" role="button" @click="removeChip(chip)"></i>
				</span>
				<a href="#" class="clear" @click.prevent="clearAll">Clear all</a>
			</div>

			<!-- Table -->
			<div class="table-wrap">
				<table class="tbl">
					<thead>
						<tr>
							<th class="sortable" @click="sortBy('name')">Account <i class="fa-solid" :class="sortIcon('name')"></i></th>
							<th v-if="cols.entity">Entity</th>
							<th v-if="cols.brands">Brands</th>
							<th v-if="cols.status">{{ singleBrand ? brandNames[singleBrand] + ' status' : 'Status by brand' }}</th>
							<th v-if="cols.plan">Plan</th>
							<th v-if="cols.leftfor">Left for</th>
							<th v-if="cols.location" class="sortable" @click="sortBy('state_abb')">Location <i class="fa-solid" :class="sortIcon('state_abb')"></i></th>
							<th class="end"></th>
						</tr>
					</thead>
					<tbody v-if="!loading">
						<tr v-for="c in rows" :key="c.id" @click="openClient(c)">
							<td>
								<div class="acct">
									<span class="avatar">{{ initials(c.name) }}</span>
									<span class="acct-name">{{ c.name }}</span>
								</div>
							</td>
							<td v-if="cols.entity" class="muted text-capitalize">{{ entityLabelFor(c.type) }}</td>
							<td v-if="cols.brands">
								<span v-for="s in c.subscriptions" :key="s.id" class="bchip" :title="brandName(s) + ' — ' + statusLabel(s.status)">
									<span class="bdot" :style="{ background: brandColor(s.brand && s.brand.key) }"></span>{{ brandLabel(s.brand && s.brand.key) }}
								</span>
							</td>
							<td v-if="cols.status">
								<template v-if="singleBrand">
									<span class="status" :class="'s-' + brandStatus(c, singleBrand)"><span class="dot"></span>{{ statusLabel(brandStatus(c, singleBrand)) }}</span>
								</template>
								<template v-else>
									<span v-for="s in c.subscriptions" :key="s.id" class="status mini" :class="'s-' + s.status" :title="brandName(s) + ' — ' + statusLabel(s.status)">
										<span class="dot"></span>{{ brandLabel(s.brand && s.brand.key) }} · {{ statusLabel(s.status) }}
									</span>
								</template>
							</td>
							<td v-if="cols.plan" class="muted">{{ planOf(c) }}</td>
							<td v-if="cols.leftfor">
								<span v-if="lostTo(c)" class="leftfor" :title="'Lost to ' + lostTo(c)"><i class="fa-solid fa-arrow-right-from-bracket"></i> {{ lostTo(c) }}</span>
							</td>
							<td v-if="cols.location" class="muted">{{ [c.county, c.state].filter(Boolean).join(', ') || '—' }}</td>
							<td class="end"><i class="fa-regular fa-pen edit"></i></td>
						</tr>
						<tr v-if="!rows.length" class="empty">
							<td :colspan="colCount">
								<i class="fa-regular fa-folder-open"></i>
								<div>No accounts match these filters.</div>
								<a href="#" @click.prevent="clearAll">Clear all filters</a>
							</td>
						</tr>
					</tbody>
					<tbody v-else>
						<tr v-for="n in 8" :key="'sk' + n" class="skeleton">
							<td :colspan="colCount"><span class="sk"></span></td>
						</tr>
					</tbody>
				</table>
			</div>

			<!-- Pagination -->
			<div v-if="pagination && pagination.total" class="pager">
				<span class="muted">Showing {{ pagination.from }}–{{ pagination.to }} of {{ pagination.total }}</span>
				<div class="pager-btns">
					<button class="pill sm" :disabled="!pagination.prev_page_url" @click="go(pagination.current_page - 1)"><i class="fa-solid fa-chevron-left"></i></button>
					<span class="muted">Page {{ pagination.current_page }} of {{ pagination.last_page }}</span>
					<button class="pill sm" :disabled="!pagination.next_page_url" @click="go(pagination.current_page + 1)"><i class="fa-solid fa-chevron-right"></i></button>
				</div>
			</div>
		</div>

		<!-- Filters modal (centered; appended to <body> on mount so it escapes transformed ancestors) -->
		<div ref="overlay">
			<div class="backdrop" :class="{ show: modal }" @click="modal = false"></div>
			<div class="modal" :class="{ show: modal }" role="dialog" aria-label="Filters">
				<div class="modal-head">
					<div><b>Filters</b><span v-if="activeChips.length" class="muted"> · {{ activeChips.length }} active</span></div>
					<i class="fa-solid fa-xmark" role="button" @click="modal = false"></i>
				</div>

				<div class="modal-body">
					<div class="fgrid">
						<section class="fsec">
							<h6>Brands</h6>
							<div class="chipwrap">
								<span v-for="b in brandPick" :key="b.key" class="tchip" :class="{ on: brandSel.includes(b.key) }" @click="toggleBrandSel(b.key)">{{ b.name }}</span>
							</div>
							<div class="seg-toggle mt" v-if="brandSel.length > 1">
								<span :class="{ on: brandMatch === 'all' }" @click="setBrandMatch('all')">Has all</span>
								<span :class="{ on: brandMatch === 'any' }" @click="setBrandMatch('any')">Has any</span>
							</div>
							<div class="hint" v-if="brandSel.length > 1">{{ brandMatch === 'all' ? 'Accounts that have every selected brand' : 'Accounts with any selected brand' }}</div>
						</section>

						<section class="fsec">
							<h6>Entity type</h6>
							<select v-model="entity" @change="reload()">
								<option value="">Any entity</option>
								<option v-for="e in entityOptions" :key="e.key" :value="e.key">{{ e.label }}<template v-if="facets.entities[e.key]"> ({{ facets.entities[e.key] }})</template></option>
							</select>
						</section>

						<section class="fsec" v-if="showPlan && planChips.length">
							<h6>Plan</h6>
							<div class="chipwrap">
								<span v-for="p in planChips" :key="p.name" class="tchip" :class="{ on: plan === p.name }" @click="togglePlan(p.name)">{{ p.name }} <span class="n">{{ p.count }}</span></span>
							</div>
						</section>

						<section class="fsec" v-if="showHeyGov">
							<h6>HeyGov products</h6>
							<div class="chipwrap">
								<span v-for="hp in hgProducts" :key="hp.p" class="tchip" :class="{ on: f[hp.p] === 'yes' }" @click="toggleProduct(hp.p)">{{ hp.label }}</span>
							</div>
						</section>

						<section class="fsec">
							<h6>Churn &amp; where they went</h6>
							<label class="ck"><input type="checkbox" v-model="dropped" @change="reload()" /><span>Partly churned — active in one brand, lost or canceled another</span></label>
							<div class="ff mt"><label>Left for competitor</label>
								<select v-model="competitor" @change="reload()">
									<option value="">Any competitor</option>
									<option v-for="(cnt, name) in facets.competitors" :key="name" :value="name">{{ name }} ({{ cnt }})</option>
								</select>
							</div>
							<div class="hint">The account list shows a "Left for" column with the destination, so you can see where churned accounts went.</div>
						</section>

						<section class="fsec">
							<h6>Geography</h6>
							<div class="ff"><label>State</label><input type="text" placeholder="e.g. WI" v-model="state" @input="debouncedReload" /></div>
							<div class="ff"><label>County</label><input type="text" placeholder="County" v-model="f.county" @input="debouncedReload" /></div>
							<div class="ff"><label>Population</label>
								<div class="row2"><input type="number" placeholder="min" v-model="ranges.frompopulation" @change="reload()" /><input type="number" placeholder="max" v-model="ranges.topopulation" @change="reload()" /></div>
							</div>
							<div class="ff"><label>Client since</label>
								<div class="row2"><input type="date" v-model="ranges.fromclient_since" @change="reload()" /><input type="date" v-model="ranges.toclient_since" @change="reload()" /></div>
							</div>
						</section>

						<section class="fsec">
							<h6>Saved views</h6>
							<div class="seglist">
								<a v-for="seg in segments" :key="'b' + seg.label" href="#" class="seg" @click.prevent="applySegment(seg)"><i class="fa-regular fa-star"></i> {{ seg.label }}</a>
								<div v-for="(v, i) in savedViews" :key="'v' + i" class="seg savedrow">
									<a href="#" @click.prevent="applyView(v)"><i class="fa-solid fa-star"></i> {{ v.label }}</a>
									<i class="fa-solid fa-xmark del" role="button" @click.stop="deleteView(i)"></i>
								</div>
							</div>
						</section>
					</div>

					<section class="fsec fullw" v-if="showTownWeb">
						<h6>Town Web details</h6>
						<div class="tw-grid">
							<div class="ff" v-for="tf in twFilters" :key="tf.p">
								<label>{{ tf.label }}</label>
								<select v-model="f[tf.p]" @change="reload()">
									<option value="">Any</option>
									<option v-for="o in tf.options" :key="o" :value="o">{{ o }}</option>
								</select>
							</div>
						</div>
					</section>
				</div>

				<div class="modal-foot">
					<button class="pill" @click="clearAll">Clear all</button>
					<button class="btn-primary" @click="modal = false">Show {{ (pagination && pagination.total) || 0 }} results</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import restApi from '../../api/index.js'
import { handleErrors } from '../../actions/helpers'
import { ENTITY_KINDS, entityLabelFor as entityLabelOf } from '../../utils/entity'

export default {
	name: 'ClientSegments',
	data() {
		return {
			brands: [],
			facets: { total: 0, brands: {}, statuses: {}, plans: {}, competitors: {}, entities: {} },
			activeBrand: 'all',
			brandSel: [],
			brandMatch: 'all',
			statuses: [],
			plan: '',
			state: '',
			search: '',
			dropped: false,
			competitor: '',
			entity: '',
			modal: false,
			colMenu: false,
			cols: { account: true, entity: true, brands: true, status: true, plan: true, leftfor: true, location: true },
			columnDefs: [
				{ key: 'account', label: 'Account', always: true },
				{ key: 'entity', label: 'Entity' },
				{ key: 'brands', label: 'Brands' },
				{ key: 'status', label: 'Status' },
				{ key: 'plan', label: 'Plan' },
				{ key: 'leftfor', label: 'Left for' },
				{ key: 'location', label: 'Location' },
			],
			savedViews: [],
			statusByBrand: {
				town_web: ['active', 'lost', 'suspended'],
				heygov: ['active', 'lost', 'suspended'],
				clerkminutes: ['trialing', 'active', 'cancelled_after_trial', 'lost', 'canceled'],
			},
			orderBy: 'name',
			direction: 'asc',
			f: {
				county: '', cms_version: '', contract_type: '', theme: '', package: '',
				hosting: '', dev_status: '', white_glove_service: '', ada_premium: '', logo_option: '',
				free_redesign: '', branded_emails: '', online_payments: '', live_stream: '',
				facebook_syndication: '', tw_client: '', tk_client: '', hg_client: '',
				heygov_311: '', heygov_reserve: '', heygov_pay: '', heygov_license: '', heygov_clerk_minutes: '',
			},
			ranges: { frompopulation: '', topopulation: '', fromclient_since: '', toclient_since: '' },
			rows: [],
			pagination: null,
			page: 1,
			loading: false,
			debounceTimer: null,
			reqSeq: 0,
			brandOrder: ['town_web', 'heygov', 'clerkminutes'],
			brandNames: { town_web: 'Town Web', heygov: 'HeyGov', clerkminutes: 'ClerkMinutes' },
			statusOptions: [
				{ value: 'active', label: 'Active' },
				{ value: 'trialing', label: 'Trialing' },
				{ value: 'cancelled_after_trial', label: 'Cancelled after trial' },
				{ value: 'lost', label: 'Lost' },
				{ value: 'suspended', label: 'Suspended' },
				{ value: 'canceled', label: 'Canceled' },
			],
			segments: [
				{ label: 'CM trials', brand: 'clerkminutes', statuses: ['trialing'] },
				{ label: 'Town Web + ClerkMinutes', brands: ['town_web', 'clerkminutes'], brandMatch: 'all' },
				{ label: 'Town Web + HeyGov', brands: ['town_web', 'heygov'], brandMatch: 'all' },
				{ label: 'Lost after trial', brand: 'clerkminutes', statuses: ['cancelled_after_trial'] },
				{ label: 'Churned a brand', dropped: true },
			],
			entityOptions: ENTITY_KINDS,
			hgProducts: [
				{ p: 'heygov_311', label: '311' },
				{ p: 'heygov_reserve', label: 'Reserve' },
				{ p: 'heygov_pay', label: 'Pay' },
				{ p: 'heygov_license', label: 'License' },
				{ p: 'heygov_clerk_minutes', label: 'ClerkMinutes' },
			],
			twFilters: [
				{ p: 'cms_version', label: 'CMS Version', options: ['juniper', 'v1', 'v2', 'v3', 'v4', 'v5 Wordpress', 'Custom Design'] },
				{ p: 'contract_type', label: 'Contract Type', options: ['1 year', '2 years', '3 years', '4 years', '5 years', 'No contract'] },
				{ p: 'theme', label: 'Theme', options: ['Premier', 'Juniper Maverick', 'Juniper Lightyear', 'Juniper Mockingbird', '2022 Theme', '2022 Mini Theme', 'TW 2020', 'TW 2019', 'TW 2018', 'Rural Rustic', 'NY Super Theme', 'Reclaimed Modern', 'Urban Modern', 'Rural Refined', 'The Thrill is Gone - OGv5', 'Custom Design', 'Other'] },
				{ p: 'package', label: 'Package', options: ['Other', 'Select', 'Deluxe', 'Super Deluxe', 'Rural', 'Select Plus', 'Elite', 'Essential', 'Community Foundation', 'Community Foundation with Payments'] },
				{ p: 'hosting', label: 'Hosting', options: ['yes', 'no'] },
				{ p: 'dev_status', label: 'Dev Status', options: ['Live', 'New - In Progress', 'Switch - In Progress', 'Upgrade - In Progress'] },
				{ p: 'white_glove_service', label: 'White Glove', options: ['Yes, email only', 'Yes, email+phone', 'no'] },
				{ p: 'ada_premium', label: 'ADA Premium', options: ['Yes', 'No'] },
				{ p: 'logo_option', label: 'Logo', options: ['Free', 'Paid', 'N/A'] },
				{ p: 'free_redesign', label: 'Free redesign', options: ['yes', 'no'] },
				{ p: 'branded_emails', label: 'Branded emails', options: ['no', 'yes,not implemented', 'yes,not implemented,archiving', 'yes,implemented', 'yes,implemented,archiving'] },
				{ p: 'online_payments', label: 'Online payments', options: ['no', 'yes,not implemented', 'yes,implemented'] },
				{ p: 'live_stream', label: 'Live stream', options: ['no', 'yes,not implemented', 'yes,implemented'] },
				{ p: 'facebook_syndication', label: 'Facebook syndication', options: ['no', 'yes,not implemented', 'yes,implemented'] },
				{ p: 'tw_client', label: 'Town Web client', options: ['yes', 'no'] },
				{ p: 'tk_client', label: 'Tweak client', options: ['yes', 'no'] },
				{ p: 'hg_client', label: 'HeyGov client', options: ['yes', 'no'] },
			],
			paramLabels: {
				county: 'County', cms_version: 'CMS', contract_type: 'Contract', theme: 'Theme',
				package: 'Package', hosting: 'Hosting', dev_status: 'Dev status', white_glove_service: 'WGS',
				ada_premium: 'ADA', logo_option: 'Logo', free_redesign: 'Free redesign', branded_emails: 'Branded emails',
				online_payments: 'Online payments', live_stream: 'Live stream', facebook_syndication: 'Facebook',
				tw_client: 'TW client', tk_client: 'Tweak', hg_client: 'HG client', heygov_311: 'HeyGov 311',
				heygov_reserve: 'HeyGov Reserve', heygov_pay: 'HeyGov Pay', heygov_license: 'HeyGov License',
				heygov_clerk_minutes: 'ClerkMinutes',
			},
		}
	},
	computed: {
		brandTabs() {
			return [{ key: 'all', name: 'All' }].concat(this.brandOrder.map(k => ({ key: k, name: this.brandNames[k] || k })))
		},
		scopeName() {
			if (this.brandSel.length) return 'Cross-brand accounts'
			return this.activeBrand === 'all' ? 'All accounts' : (this.brandNames[this.activeBrand] || this.activeBrand) + ' accounts'
		},
		brandScope() {
			if (this.brandSel.length) return this.brandSel
			if (this.activeBrand !== 'all') return [this.activeBrand]
			return ['town_web', 'heygov', 'clerkminutes']
		},
		singleBrand() {
			if (this.brandSel.length === 1) return this.brandSel[0]
			if (this.brandSel.length === 0 && this.activeBrand !== 'all') return this.activeBrand
			return null
		},
		showTownWeb() { return this.brandScope.includes('town_web') },
		showHeyGov() { return this.brandScope.includes('heygov') },
		showPlan() { return this.brandScope.includes('clerkminutes') },
		brandPick() { return this.brandOrder.map(k => ({ key: k, name: this.brandNames[k] || k })) },
		planChips() {
			return Object.keys(this.facets.plans || {}).map(name => ({ name, count: this.facets.plans[name] }))
		},
		visibleStatuses() {
			const allow = new Set()
			this.brandScope.forEach(k => (this.statusByBrand[k] || []).forEach(s => allow.add(s)))
			return this.statusOptions.filter(o => allow.has(o.value))
		},
		colCount() {
			return 2 + this.columnDefs.filter(c => !c.always).reduce((n, c) => n + (this.cols[c.key] ? 1 : 0), 0)
		},
		activeChips() {
			const chips = []
			if (this.brandSel.length) {
				const join = this.brandMatch === 'all' ? ' + ' : ' / '
				chips.push({ key: 'brands', label: 'Brands: ' + this.brandSel.map(k => this.brandNames[k] || k).join(join), type: 'brands' })
			}
			this.statuses.forEach(s => chips.push({ key: 'status:' + s, label: 'Status: ' + this.statusLabel(s), type: 'status', value: s }))
			if (this.entity) chips.push({ key: 'entity', label: 'Entity: ' + this.entityLabel(this.entity), type: 'entity' })
			if (this.plan) chips.push({ key: 'plan', label: 'Plan: ' + this.plan, type: 'plan' })
			if (this.state) chips.push({ key: 'state', label: 'State: ' + this.state, type: 'state' })
			if (this.competitor) chips.push({ key: 'competitor', label: 'Left for: ' + this.competitor, type: 'competitor' })
			if (this.dropped) chips.push({ key: 'dropped', label: 'Churned a brand', type: 'dropped' })
			Object.keys(this.f).forEach(k => { if (this.f[k]) chips.push({ key: 'f:' + k, label: this.labelFor(k) + ': ' + this.f[k], type: 'f', value: k }) })
			if (this.ranges.frompopulation || this.ranges.topopulation) chips.push({ key: 'pop', label: 'Pop ' + (this.ranges.frompopulation || '0') + '–' + (this.ranges.topopulation || '∞'), type: 'pop' })
			if (this.ranges.fromclient_since || this.ranges.toclient_since) chips.push({ key: 'since', label: 'Since ' + (this.ranges.fromclient_since || '…') + ' → ' + (this.ranges.toclient_since || '…'), type: 'since' })
			return chips
		},
	},
	created() {
		this.loadPrefs()
		this.bootWhenAuthed()
	},
	mounted() {
		const el = this.$refs.overlay
		if (el && el.parentNode !== document.body) document.body.appendChild(el)
	},
	beforeDestroy() {
		const el = this.$refs.overlay
		if (el && el.parentNode) el.parentNode.removeChild(el)
	},
	methods: {
		bootWhenAuthed() {
			const boot = () => { this.getBrands(); this.getFacets(); this.fetchData() }
			if (this.$store.state.token) { boot(); return }
			const stop = this.$store.watch(st => st.token, t => { if (t) { stop(); boot() } })
		},
		labelFor(k) { return this.paramLabels[k] || k },
		loadPrefs() {
			try { const c = JSON.parse(localStorage.getItem('accounts-cols')); if (c) this.cols = Object.assign({}, this.cols, c) } catch (e) { /* ignore */ }
			try { const v = JSON.parse(localStorage.getItem('accounts-views')); if (Array.isArray(v)) this.savedViews = v } catch (e) { /* ignore */ }
		},
		saveCols() { localStorage.setItem('accounts-cols', JSON.stringify(this.cols)) },
		allowedStatuses() {
			const allow = new Set()
			this.brandScope.forEach(k => (this.statusByBrand[k] || []).forEach(s => allow.add(s)))
			return allow
		},
		pruneStatuses() {
			const allow = this.allowedStatuses()
			this.statuses = this.statuses.filter(s => allow.has(s))
		},
		captureState() {
			return {
				activeBrand: this.activeBrand, brandSel: [...this.brandSel], brandMatch: this.brandMatch,
				statuses: [...this.statuses], entity: this.entity, plan: this.plan, state: this.state,
				competitor: this.competitor, dropped: this.dropped, search: this.search,
				f: Object.assign({}, this.f), ranges: Object.assign({}, this.ranges),
			}
		},
		saveView() {
			this.$swal.fire({ icon: 'question', title: 'Save this view?', input: 'text', inputPlaceholder: 'View name', showCancelButton: true })
				.then(r => {
					if (r.isConfirmed && r.value) {
						this.savedViews.push({ label: r.value, state: this.captureState() })
						localStorage.setItem('accounts-views', JSON.stringify(this.savedViews))
						this.$swal.fire({ icon: 'success', title: 'View saved', timer: 1400, toast: true, position: 'top-end', showConfirmButton: false })
					}
				})
		},
		applyView(v) {
			const s = v.state || {}
			this.activeBrand = s.activeBrand || 'all'
			this.brandSel = Array.isArray(s.brandSel) ? [...s.brandSel] : []
			this.brandMatch = s.brandMatch || 'all'
			this.statuses = Array.isArray(s.statuses) ? [...s.statuses] : []
			this.entity = s.entity || ''
			this.plan = s.plan || ''
			this.state = s.state || ''
			this.competitor = s.competitor || ''
			this.dropped = !!s.dropped
			this.search = s.search || ''
			const fBlank = { county: '', cms_version: '', contract_type: '', theme: '', package: '', hosting: '', dev_status: '', white_glove_service: '', ada_premium: '', logo_option: '', free_redesign: '', branded_emails: '', online_payments: '', live_stream: '', facebook_syndication: '', tw_client: '', tk_client: '', hg_client: '', heygov_311: '', heygov_reserve: '', heygov_pay: '', heygov_license: '', heygov_clerk_minutes: '' }
			this.f = Object.assign(fBlank, s.f || {})
			this.ranges = Object.assign({ frompopulation: '', topopulation: '', fromclient_since: '', toclient_since: '' }, s.ranges || {})
			this.modal = false
			this.getFacets()
			this.reload()
		},
		deleteView(i) {
			this.savedViews.splice(i, 1)
			localStorage.setItem('accounts-views', JSON.stringify(this.savedViews))
		},
		entityLabel(k) { const e = this.entityOptions.find(x => x.key === k); return e ? e.label : k },
		tabCount(key) { return key === 'all' ? (this.facets.total || 0) : (this.facets.brands[key] || 0) },
		getBrands() { restApi.get('/brands').then(({ data }) => { this.brands = data }).catch(() => {}) },
		getFacets() {
			const params = this.activeBrand !== 'all' ? { brand: this.activeBrand } : {}
			restApi.get('/client-segments/facets', { params }).then(({ data }) => {
				this.facets = {
					total: data.total || 0, brands: data.brands || {}, statuses: data.statuses || {},
					plans: data.plans || {}, competitors: data.competitors || {}, entities: data.entities || {},
				}
			}).catch(() => {})
		},
		isTabActive(key) { return this.brandSel.length ? key === 'all' : this.activeBrand === key },
		selectBrand(key) {
			this.activeBrand = key
			this.brandSel = []
			this.plan = ''
			this.page = 1
			this.pruneStatuses()
			this.getFacets()
			this.fetchData()
		},
		toggleBrandSel(k) {
			const i = this.brandSel.indexOf(k)
			if (i === -1) this.brandSel.push(k)
			else this.brandSel.splice(i, 1)
			this.activeBrand = 'all'
			this.plan = ''
			this.pruneStatuses()
			this.getFacets()
			this.reload()
		},
		setBrandMatch(m) { this.brandMatch = m; this.reload() },
		toggleStatus(v) {
			const i = this.statuses.indexOf(v)
			if (i === -1) this.statuses.push(v)
			else this.statuses.splice(i, 1)
			this.reload()
		},
		togglePlan(name) { this.plan = this.plan === name ? '' : name; this.reload() },
		toggleProduct(p) { this.f[p] = this.f[p] === 'yes' ? '' : 'yes'; this.reload() },
		applySegment(seg) {
			if (seg.brands) {
				this.brandSel = [...seg.brands]
				this.brandMatch = seg.brandMatch || 'all'
				this.activeBrand = 'all'
			} else {
				this.brandSel = []
				this.activeBrand = seg.brand || this.activeBrand
			}
			this.statuses = seg.statuses ? [...seg.statuses] : []
			this.dropped = !!seg.dropped
			this.plan = ''
			this.modal = false
			this.getFacets()
			this.reload()
		},
		sortBy(col) {
			if (this.orderBy === col) this.direction = this.direction === 'asc' ? 'desc' : 'asc'
			else { this.orderBy = col; this.direction = 'asc' }
			this.reload()
		},
		sortIcon(col) {
			if (this.orderBy !== col) return 'fa-sort faint'
			return this.direction === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
		},
		removeChip(chip) {
			if (chip.type === 'status') this.statuses = this.statuses.filter(s => s !== chip.value)
			else if (chip.type === 'brands') this.brandSel = []
			else if (chip.type === 'entity') this.entity = ''
			else if (chip.type === 'plan') this.plan = ''
			else if (chip.type === 'state') this.state = ''
			else if (chip.type === 'competitor') this.competitor = ''
			else if (chip.type === 'dropped') this.dropped = false
			else if (chip.type === 'f') this.f[chip.value] = ''
			else if (chip.type === 'pop') { this.ranges.frompopulation = ''; this.ranges.topopulation = '' }
			else if (chip.type === 'since') { this.ranges.fromclient_since = ''; this.ranges.toclient_since = '' }
			this.reload()
		},
		clearAll() {
			this.statuses = []; this.plan = ''; this.state = ''; this.competitor = ''; this.dropped = false; this.search = ''; this.entity = ''
			this.brandSel = []; this.brandMatch = 'all'
			Object.keys(this.f).forEach(k => { this.f[k] = '' })
			Object.keys(this.ranges).forEach(k => { this.ranges[k] = '' })
			this.reload()
		},
		debouncedReload() {
			clearTimeout(this.debounceTimer)
			this.debounceTimer = setTimeout(() => this.reload(), 350)
		},
		reload() { this.page = 1; this.fetchData() },
		go(p) { this.page = p; this.fetchData() },
		params() {
			const p = { perPage: 25, page: this.page, orderBy: this.orderBy, direction: this.direction }
			if (this.brandSel.length) { p.brands = this.brandSel.join(','); p.brand_match = this.brandMatch }
			else if (this.activeBrand !== 'all') p.brand = this.activeBrand
			if (this.statuses.length) p.status = this.statuses.join(',')
			if (this.entity) p.entity = this.entity
			if (this.plan) p.plan = this.plan
			if (this.state) p.state = this.state
			if (this.search) p.search = this.search
			if (this.dropped) p.dropped = 1
			if (this.competitor) p.competitor = this.competitor
			Object.keys(this.f).forEach(k => { if (this.f[k]) p[k] = this.f[k] })
			Object.keys(this.ranges).forEach(k => { if (this.ranges[k] !== '' && this.ranges[k] != null) p[k] = this.ranges[k] })
			return p
		},
		fetchData() {
			this.loading = true
			const seq = (this.reqSeq = this.reqSeq + 1)
			restApi.get('/client-segments', { params: this.params() })
				.then(({ data }) => {
					if (seq !== this.reqSeq) return
					this.rows = data.data || []
					this.pagination = data
					this.loading = false
				})
				.catch(error => {
					if (seq !== this.reqSeq) return
					this.loading = false
					this.$swal.fire({ icon: 'error', title: handleErrors(error, ''), timer: 6000, toast: true, position: 'top-end', showConfirmButton: false })
				})
		},
		openClient(c) { this.$router.push('/clients/edit/' + c.id) },
		saveSegment() {
			this.$swal.fire({ icon: 'question', title: 'Save this view?', input: 'text', inputPlaceholder: 'View name', showCancelButton: true })
				.then(r => { if (r.isConfirmed && r.value) this.segments.push({ label: r.value, brand: this.activeBrand, brands: this.brandSel.length ? [...this.brandSel] : null, brandMatch: this.brandMatch, statuses: [...this.statuses], dropped: this.dropped }) })
		},
		comingSoon(what) { this.$swal.fire({ icon: 'info', title: what, text: 'Coming soon', timer: 1600, toast: true, position: 'top-end', showConfirmButton: false }) },
		exportCsv() {
			const head = ['Name', 'Entity', 'County', 'State', 'Status', 'Plan', 'Left for']
			const lines = [head.join(',')].concat(this.rows.map(c => [c.name, this.entityLabelFor(c.type), c.county, c.state, this.singleBrand ? this.statusLabel(this.brandStatus(c, this.singleBrand)) : this.perBrandText(c), this.planOf(c), this.lostTo(c) || '']
				.map(v => '"' + String(v == null ? '' : v).replace(/"/g, '""') + '"').join(',')))
			const blob = new Blob([lines.join('\n')], { type: 'text/csv' })
			const a = document.createElement('a')
			a.href = URL.createObjectURL(blob); a.download = 'accounts.csv'; a.click(); URL.revokeObjectURL(a.href)
		},
		subFor(c, key) { return (c.subscriptions || []).find(s => s.brand && s.brand.key === key) || null },
		brandStatus(c, key) { const s = this.subFor(c, key); return s ? s.status : this.rollup(c) },
		perBrandText(c) { return (c.subscriptions || []).map(s => this.brandLabel(s.brand && s.brand.key) + ' ' + this.statusLabel(s.status)).join('; ') },
		rollup(c) {
			const order = ['active', 'trialing', 'suspended', 'lost', 'cancelled_after_trial', 'canceled']
			const have = (c.subscriptions || []).map(s => s.status)
			return order.find(s => have.includes(s)) || 'canceled'
		},
		lostTo(c) {
			const m = c.municipality
			if (!m) return null
			return m.competitor || m.competitor_hg || null
		},
		planOf(c) {
			const subs = (c.subscriptions || []).filter(s => s.plan)
			return subs.length ? subs[0].plan : '—'
		},
		entityLabelFor(type) { return entityLabelOf(type) },
		statusLabel(s) {
			return { active: 'Active', trialing: 'Trialing', cancelled_after_trial: 'Cancelled after trial', lost: 'Lost', suspended: 'Suspended', canceled: 'Canceled (unknown)' }[s] || '—'
		},
		brandLabel(key) { return { town_web: 'TW', heygov: 'HG', clerkminutes: 'CM' }[key] || (key || '?') },
		brandName(s) { return (s && s.brand && s.brand.name) || this.brandLabel(s && s.brand && s.brand.key) },
		brandColor(key) { return { town_web: '#2563eb', heygov: '#7a5af8', clerkminutes: '#12b76a' }[key] || '#98a2b3' },
		initials(name) {
			const parts = String(name || '?').trim().split(/\s+/)
			return ((parts[0] || '')[0] || '' + (parts[1] ? parts[1][0] : '')).toUpperCase() || '?'
		},
	},
}
</script>

<style scoped>
.cs {
	--bg: #f6f7f9; --card: #fff; --bd: #e7e9ee; --bd-soft: #f0f1f4;
	--ink: #101828; --ink2: #475467; --muted: #667085; --faint: #98a2b3;
	--blue: #2563eb; --blue-soft: #eaf0ff;
	padding: 22px 26px; background: var(--bg); min-height: 100%;
	font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: var(--ink);
}
.cs-head { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 18px; }
.cs-title { font-size: 28px; font-weight: 700; letter-spacing: -0.02em; margin: 0; }
.cs-sub { color: var(--muted); font-size: 14px; margin: 4px 0 0; }
.cs-actions { display: flex; gap: 8px; }

.pill { display: inline-flex; align-items: center; gap: 7px; background: #fff; border: 1px solid var(--bd); color: var(--ink2);
	border-radius: 999px; padding: 8px 15px; font-size: 13px; font-weight: 500; cursor: pointer; transition: all .12s; white-space: nowrap; }
.pill:hover { background: #f9fafb; border-color: #d7dae0; }
.pill.on { background: var(--blue-soft); border-color: #c2d4fb; color: var(--blue); }
.pill .badge { background: var(--blue); color: #fff; border-radius: 999px; font-size: 11px; padding: 1px 7px; font-weight: 600; }
.pill.sm { padding: 6px 10px; }
.pill:disabled { opacity: .45; cursor: not-allowed; }
.btn-primary { background: var(--blue); color: #fff; border: none; border-radius: 999px; padding: 10px 18px; font-size: 13px; font-weight: 600; cursor: pointer; }
.btn-primary:hover { background: #1d4ed8; }
.pill.primary-ghost { background: var(--blue-soft); border-color: #c2d4fb; color: var(--blue); font-weight: 600; }
.pill.primary-ghost:hover { background: #dfe8ff; }
.pill.clear-btn { color: #b42318; border-color: #f3d3cf; }
.pill.clear-btn:hover { background: #fef3f2; }
.colwrap { position: relative; }
.colmenu-back { position: fixed; inset: 0; z-index: 20; }
.colmenu { position: absolute; top: calc(100% + 6px); left: 0; z-index: 21; background: #fff; border: 1px solid var(--bd); border-radius: 12px; box-shadow: 0 8px 24px rgba(16,24,40,.14); padding: 10px 12px; min-width: 180px; }
.colmenu-title { font-size: 11px; font-weight: 600; color: var(--muted); text-transform: uppercase; letter-spacing: .03em; margin-bottom: 8px; }
.colrow { display: flex; align-items: center; gap: 8px; font-size: 13.5px; color: var(--ink2); padding: 4px 0; cursor: pointer; margin: 0; }
.savedrow { display: flex; align-items: center; justify-content: space-between; }
.savedrow a { text-decoration: none; color: var(--ink2); } .savedrow a:hover { color: var(--blue); }
.savedrow .del { color: var(--faint); cursor: pointer; font-size: 12px; } .savedrow .del:hover { color: #b42318; }

.tabs { display: flex; gap: 4px; border-bottom: 1px solid var(--bd); margin-bottom: 16px; overflow-x: auto; }
.tab { display: inline-flex; align-items: center; gap: 7px; padding: 11px 14px; font-size: 14px; font-weight: 500; color: var(--ink2);
	text-decoration: none; border-bottom: 2px solid transparent; margin-bottom: -1px; white-space: nowrap; }
.tab:hover { color: var(--ink); }
.tab.active { color: var(--blue); border-bottom-color: var(--blue); font-weight: 600; }
.cpill { background: #f2f4f7; color: var(--ink2); border-radius: 999px; font-size: 11px; font-weight: 600; padding: 1px 8px; }
.tab.active .cpill { background: var(--blue-soft); color: var(--blue); }

.card { background: var(--card); border: 1px solid var(--bd); border-radius: 16px; box-shadow: 0 1px 2px rgba(16,24,40,.04); overflow: hidden; }
.card-head { display: flex; justify-content: space-between; align-items: center; padding: 18px 20px; }
.card-title { font-size: 16px; font-weight: 700; }
.card-sub { color: var(--muted); font-size: 13px; margin-top: 2px; }

.toolbar { display: flex; align-items: center; gap: 12px; padding: 0 20px 14px; flex-wrap: wrap; }
.search { display: flex; align-items: center; gap: 8px; background: #fff; border: 1px solid var(--bd); border-radius: 10px; padding: 8px 12px; min-width: 280px; flex: 0 0 auto; }
.search i { color: var(--faint); font-size: 13px; }
.search input { border: none; outline: none; font-size: 14px; width: 230px; color: var(--ink); background: transparent; }
.status-quick { display: flex; gap: 6px; flex-wrap: wrap; }
.spill { display: inline-flex; align-items: center; gap: 6px; border: 1px solid var(--bd); border-radius: 999px; padding: 6px 11px; font-size: 12.5px; font-weight: 500; color: var(--ink2); cursor: pointer; transition: all .12s; }
.spill .dot { width: 7px; height: 7px; border-radius: 50%; background: #cbd2dc; }
.spill .n { color: var(--faint); font-size: 11px; }
.spill:hover { border-color: #d7dae0; background: #f9fafb; }
.spill.on { border-color: currentColor; }
.spill.s-active.on { color: #067647; background: #ecfdf3; } .spill.s-active .dot { background: #12b76a; }
.spill.s-trialing.on { color: #b54708; background: #fffaeb; } .spill.s-trialing .dot { background: #f79009; }
.spill.s-cancelled_after_trial.on { color: #5925dc; background: #f4f3ff; } .spill.s-cancelled_after_trial .dot { background: #7a5af8; }
.spill.s-lost.on { color: #b42318; background: #fef3f2; } .spill.s-lost .dot { background: #f04438; }
.spill.s-suspended.on { color: #175cd3; background: #eff8ff; } .spill.s-suspended .dot { background: #2e90fa; }
.spill.s-canceled.on { color: #475467; background: #f2f4f7; } .spill.s-canceled .dot { background: #98a2b3; }

.chips { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; padding: 0 20px 14px; }
.chip { display: inline-flex; align-items: center; gap: 7px; background: var(--blue-soft); color: var(--blue); border-radius: 8px; padding: 4px 10px; font-size: 12.5px; font-weight: 500; }
.chip i { cursor: pointer; font-size: 11px; opacity: .7; } .chip i:hover { opacity: 1; }
.clear { font-size: 12.5px; color: var(--muted); text-decoration: none; } .clear:hover { color: var(--ink); }

.table-wrap { overflow-x: auto; }
.tbl { width: 100%; border-collapse: collapse; }
.tbl thead th { text-align: left; font-size: 12px; font-weight: 600; color: var(--muted); padding: 10px 16px; border-top: 1px solid var(--bd); border-bottom: 1px solid var(--bd); background: #fcfcfd; white-space: nowrap; }
.tbl thead th.sortable { cursor: pointer; user-select: none; } .tbl thead th.sortable:hover { color: var(--ink2); }
.tbl thead th i { font-size: 10px; margin-left: 3px; color: var(--ink2); } .tbl thead th i.faint { color: #d0d5dd; }
.tbl thead th.end { width: 44px; }
.tbl tbody td { padding: 12px 16px; border-bottom: 1px solid var(--bd-soft); font-size: 14px; vertical-align: middle; }
.tbl tbody tr { cursor: pointer; } .tbl tbody tr:hover td { background: #fafbfc; }
.tbl td.muted { color: var(--ink2); }
.acct { display: flex; align-items: center; gap: 10px; }
.avatar { width: 32px; height: 32px; border-radius: 9px; display: inline-flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; color: var(--ink2); background: #eef1f6; flex: 0 0 auto; }
.acct-name { font-weight: 600; color: var(--ink); }
.bchip { display: inline-flex; align-items: center; gap: 5px; border: 1px solid var(--bd); border-radius: 7px; padding: 2px 7px; margin-right: 4px; font-size: 11px; font-weight: 600; color: var(--ink2); }
.bdot { width: 7px; height: 7px; border-radius: 50%; }
.status { display: inline-flex; align-items: center; gap: 6px; border-radius: 999px; padding: 3px 10px; font-size: 12px; font-weight: 600; }
.status.mini { padding: 2px 8px; font-size: 11px; margin: 1px 4px 1px 0; }
.status .dot { width: 7px; height: 7px; border-radius: 50%; }
.status.s-active { background: #ecfdf3; color: #067647; } .status.s-active .dot { background: #12b76a; }
.status.s-trialing { background: #fffaeb; color: #b54708; } .status.s-trialing .dot { background: #f79009; }
.status.s-cancelled_after_trial { background: #f4f3ff; color: #5925dc; } .status.s-cancelled_after_trial .dot { background: #7a5af8; }
.status.s-lost { background: #fef3f2; color: #b42318; } .status.s-lost .dot { background: #f04438; }
.status.s-suspended { background: #eff8ff; color: #175cd3; } .status.s-suspended .dot { background: #2e90fa; }
.status.s-canceled { background: #f2f4f7; color: #475467; } .status.s-canceled .dot { background: #98a2b3; }
.leftfor { display: inline-flex; align-items: center; gap: 5px; background: #fef3f2; color: #b42318; border-radius: 7px; padding: 3px 9px; font-size: 12px; font-weight: 600; }
.edit { color: var(--faint); } .tbl tbody tr:hover .edit { color: var(--blue); }
.tbl tr.empty td { text-align: center; padding: 48px 16px; color: var(--muted); }
.tbl tr.empty i { font-size: 26px; color: #d0d5dd; display: block; margin-bottom: 8px; }
.tbl tr.empty a { color: var(--blue); text-decoration: none; font-size: 13px; }
.skeleton .sk { display: block; height: 14px; border-radius: 6px; background: linear-gradient(90deg,#f0f1f4,#f7f8fa,#f0f1f4); }

.pager { display: flex; justify-content: space-between; align-items: center; padding: 14px 20px; border-top: 1px solid var(--bd); font-size: 13px; }
.pager .muted { color: var(--muted); }
.pager-btns { display: flex; align-items: center; gap: 10px; }

/* overlay is relocated under <body> (outside .cs) — carry its own tokens so var(--bd)/--muted/etc. resolve */
.backdrop, .modal { --bg: #f6f7f9; --card: #fff; --bd: #e7e9ee; --bd-soft: #f0f1f4; --ink: #101828; --ink2: #475467; --muted: #667085; --faint: #98a2b3; --blue: #2563eb; --blue-soft: #eaf0ff; }
.backdrop { position: fixed; inset: 0; background: rgba(16,24,40,.45); z-index: 1040; opacity: 0; pointer-events: none; transition: opacity .18s; }
.backdrop.show { opacity: 1; pointer-events: auto; }
.modal { position: fixed; top: 50%; left: 50%; width: min(780px, 94vw); max-height: 86vh; background: #fff; border-radius: 16px;
	box-shadow: 0 24px 60px rgba(16,24,40,.24); z-index: 1050; display: flex; flex-direction: column;
	transform: translate(-50%, -48%) scale(.98); opacity: 0; pointer-events: none; transition: opacity .18s, transform .18s; }
.modal.show { transform: translate(-50%, -50%) scale(1); opacity: 1; pointer-events: auto; }
.modal-head { display: flex; justify-content: space-between; align-items: center; padding: 16px 22px; border-bottom: 1px solid var(--bd); font-size: 16px; }
.modal-head i { cursor: pointer; color: var(--muted); font-size: 18px; } .modal-head .muted { color: var(--muted); font-weight: 400; font-size: 13px; }
.modal-body { overflow-y: auto; padding: 8px 22px 20px; }
.fgrid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 28px; }
.fsec { padding: 16px 0; border-bottom: 1px solid var(--bd-soft); }
.fsec.fullw { border-bottom: none; }
.fsec h6 { font-size: 12px; font-weight: 700; color: var(--muted); text-transform: uppercase; letter-spacing: .03em; margin: 0 0 10px; }
.seglist { display: flex; flex-direction: column; gap: 2px; }
.seg { font-size: 13.5px; color: var(--ink2); text-decoration: none; padding: 4px 0; } .seg:hover { color: var(--blue); } .seg i { color: #f79009; margin-right: 6px; }
.chipwrap { display: flex; flex-wrap: wrap; gap: 7px; }
.tchip { border: 1px solid var(--bd); border-radius: 8px; padding: 5px 11px; font-size: 12.5px; color: var(--ink2); cursor: pointer; transition: all .12s; }
.tchip:hover { border-color: #d7dae0; } .tchip.on { background: var(--blue-soft); border-color: #c2d4fb; color: var(--blue); font-weight: 600; }
.tchip .n { color: var(--faint); font-size: 11px; } .tchip.on .n { color: var(--blue); }
.seg-toggle { display: inline-flex; border: 1px solid var(--bd); border-radius: 8px; overflow: hidden; font-size: 12px; }
.seg-toggle span { padding: 5px 12px; cursor: pointer; color: var(--ink2); }
.seg-toggle span.on { background: var(--blue); color: #fff; }
.mt { margin-top: 10px; }
.hint { font-size: 11.5px; color: var(--muted); margin-top: 8px; line-height: 1.4; }
.ff { margin-bottom: 10px; }
.ff label { display: block; font-size: 12px; color: var(--muted); margin-bottom: 4px; }
.ff select, .ff input, .fsec > select { width: 100%; border: 1px solid var(--bd); border-radius: 9px; padding: 8px 10px; font-size: 13.5px; color: var(--ink); outline: none; background: #fff; }
.ff select:focus, .ff input:focus, .fsec > select:focus, .search input:focus { border-color: var(--blue); }
.row2 { display: flex; gap: 8px; } .row2 input { width: 100%; }
.ck { display: flex; align-items: flex-start; gap: 8px; font-size: 13px; color: var(--ink2); cursor: pointer; margin: 0; }
.ck input { margin-top: 2px; }
.tw-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px 14px; }
.modal-foot { display: flex; justify-content: space-between; align-items: center; gap: 10px; padding: 14px 22px; border-top: 1px solid var(--bd); }
@media (max-width: 640px) { .fgrid { grid-template-columns: 1fr; } .tw-grid { grid-template-columns: 1fr 1fr; } }
</style>
